import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeContext';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import PrivateRoute from './components/PrivateRoute';
import WelcomeRedirectRoute from './components/WelcomeRedirectRoute';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ConversationsPage from './pages/ConversationsPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminDashboard from './pages/AdminDashboard';
import ChangePlanPage from './pages/ChangePlanPage';
import ComprarCreditosPage from './pages/ComprarCreditosPage';
import ActivateAccountPage from './pages/ActivateAccountPage';
import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DPAPage from './pages/DPAPage';
import PaymentPage from './pages/PaymentPage';
import PublicDemoPage from './pages/PublicDemoPage';
import HomologationPage from './pages/HomologationPage';
import PublicLeadViewPage from './pages/PublicLeadViewPage';
import CampaignRedirectPage from './pages/CampaignRedirectPage';

const AdminRedirectRoute: React.FC = () => {
  const { user, isAuthenticated, isInitializing, currentOrganization } = useAuth();

  if (isInitializing) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Verificar se o usuário precisa selecionar um plano
  const needsPlanSelection = !currentOrganization?.currentPlan || localStorage.getItem('needsPlanSelection') === 'true';
  if (needsPlanSelection) {
    return <Navigate to="/change-plan" replace state={{ forceSelection: true }} />;
  }

  if (user?.role === 'admin' || user?.role === 'whitelabel' || user?.isWhiteLabel) {
    return <Navigate to="/admin" replace />;
  }

  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <ThemeModeProvider>
      <ThemeAwareToaster />
    </ThemeModeProvider>
  );
}

const ThemeAwareToaster: React.FC = () => {
  const { mode } = useThemeMode();

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: mode === 'dark'
              ? '#1a1a1a'
              : 'linear-gradient(135deg, #1a1a1a 0%, #1a1a1a 100%)',
            color: '#fff',
            borderRadius: '12px',
            fontSize: '14px',
            padding: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            border: mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
          },
          success: {
            iconTheme: {
              primary: '#13B497',
              secondary: '#fff',
            },
            style: {
              background: 'linear-gradient(135deg, #13B497 0%, #0F7B6C 100%)',
              color: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#c62828',
              secondary: '#fff',
            },
            style: {
              background: 'linear-gradient(135deg, #ff5f52 0%, #c62828 100%)',
              color: '#fff',
            },
          },
        }}
      />
      <AuthProvider>
        <PermissionsProvider>
          <NotificationProvider>
            <Router>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/activate" element={<ActivateAccountPage />} />
                <Route path="/termos-de-uso" element={<TermsOfUse />} />
                <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />
                <Route path="/dpa" element={<DPAPage />} />
                <Route path="/demo" element={<PublicDemoPage />} />
                <Route path="/public" element={<PublicDemoPage />} />
                <Route path="/homologation/:organizationId/:agentId" element={<HomologationPage />} />
                <Route path="/view-lead/:organizationId/:leadId" element={<PublicLeadViewPage />} />
                <Route path="/campaign" element={<CampaignRedirectPage />} />
                <Route path="/welcome" element={<Navigate to="/change-plan" replace />} />
                <Route path="/change-plan" element={
                  <PrivateRoute>
                    <ChangePlanPage />
                  </PrivateRoute>
                } />
                <Route path="/payment" element={
                  <PrivateRoute>
                    <PaymentPage />
                  </PrivateRoute>
                } />
                <Route path="/comprar-creditos" element={
                  <PrivateRoute>
                    <ComprarCreditosPage />
                  </PrivateRoute>
                } />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route
                  path="/dashboard"
                  element={
                    <WelcomeRedirectRoute>
                      <PrivateRoute>
                        <DashboardPage />
                      </PrivateRoute>
                    </WelcomeRedirectRoute>
                  }
                />
                <Route path="/dashboard/lead/:leadId" element={
                  <WelcomeRedirectRoute>
                    <PrivateRoute>
                      <DashboardPage />
                    </PrivateRoute>
                  </WelcomeRedirectRoute>
                } />
                <Route path="/conversations" element={
                  <WelcomeRedirectRoute>
                    <PrivateRoute requiredRoles={['admin', 'gerente', 'vendedor', 'whitelabel']}>
                      <ConversationsPage />
                    </PrivateRoute>
                  </WelcomeRedirectRoute>
                } />
                <Route path="/admin" element={
                  <WelcomeRedirectRoute>
                    <PrivateRoute requiredRoles={['admin', 'whitelabel']}>
                      <AdminDashboard />
                    </PrivateRoute>
                  </WelcomeRedirectRoute>
                } />
                <Route path="/admin/*" element={
                  <WelcomeRedirectRoute>
                    <PrivateRoute requiredRoles={['admin', 'whitelabel']}>
                      <AdminDashboard />
                    </PrivateRoute>
                  </WelcomeRedirectRoute>
                } />
                <Route path="/" element={<AdminRedirectRoute />} />
              </Routes>
            </Router>
          </NotificationProvider>
        </PermissionsProvider>
      </AuthProvider>
    </>
  );
}

export default App;
